import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { FlexiLogic } from '@customer-apps/flexi-logic';
import { ContextModelService } from './context-model.service';
import { FlowUtils, uniqueById } from '../utils';
import {
    ApplicationFlow,
    Block,
    BlockDecisionPath,
    BlockParameter,
    BlockType,
    CompiledFlowBlock,
    ContextModelState,
    FrontendConfiguration,
    TemplateSlot
} from '../interfaces';
import {
    CompiledPath,
    DefaultSession,
    PathResolver,
    Session,
    Token,
    TokenKind,
    NormalizedToken,
    createPropToken,
    createIndexToken
} from '@customer-apps/flexi-paths';
import { getNestedValue, setNestedValue, getDefaultsByKeys } from '../utils';
import { QuestionFlowStateCleanupService } from './question-flow-state-cleanup.service';

@Injectable()
export class QuestionFlowService {
    private _contextModelService = inject(ContextModelService);
    private _stateCleanupService = inject(QuestionFlowStateCleanupService);
    private _pathResolver = new PathResolver();
    readonly contextStateVersion = signal(0);
    private readonly _slotsDisabledSignals = new Map<string, WritableSignal<boolean>>();

    /**
     * Holds the last value entered into a slot while it was inactive
     * (`isInactive` evaluated to `true`) as a pending value. Inactive slots must not
     * write to the context model state, but the pending value has to be preserved so
     * it can be flushed once the controlling flexi-logic dependency turns the slot active.
     * Keyed by slot reference — slot IDs are not unique across flow blocks.
     */
    private readonly _pendingInactiveSlotValues = new Map<TemplateSlot, unknown>();

    getSlotDisabledSignal(slotId: string): WritableSignal<boolean> {
        let sig = this._slotsDisabledSignals.get(slotId);
        if (!sig) {
            sig = signal(false);
            this._slotsDisabledSignals.set(slotId, sig);
        }
        return sig;
    }

    /**
     * `true` when the loaded {@link FrontendConfiguration} is invalid or
     * in the legacy format (only `definitionBlocks`, no `compiledFlowBlocks`). Host components
     * read this signal to render an "invalid or outdated configuration" alert instead of
     * attempting to render the flow.
     */
    readonly invalidConfiguration = signal(false);

    private _pathSession: Session | undefined;
    private _accordionBlocksIdsFlow: string[] = [];
    private _flowData!: ApplicationFlow | undefined;
    private _blocks!: Block[];
    private _decisionBlocksOutput: { [key: string]: string } = {};
    private _reversedDecisionBlocksOutput: { [key: string]: string } = {};
    private _decisionBlocks: Block[] = [];
    private _hiddenContainers: { [key: string]: boolean } = {};
    private _hiddenAccordions: { [key: string]: boolean } = {};
    private _hiddenBlocks: { [key: string]: boolean } = {};

    private _firstQuestionBlock: Block;
    private _questionsPath: Block[] = [];

    private _currentContainerBlockId!: string;
    private _currentAccordionBlockId!: string;

    private _compiledFlowBlocks: { [flowBlockId: string]: CompiledFlowBlock } | undefined;

    private setCurrentContainerBlockId(id: string) {
        this._currentContainerBlockId = id;
    }

    private setCurrentAccordionBlockId(id: string) {
        this._currentAccordionBlockId = id;
    }

    get currentContainerBlockId() {
        return this._currentContainerBlockId;
    }

    get currentAccordionBlockId() {
        return this._currentAccordionBlockId;
    }

    get hiddenContainers() {
        return this._hiddenContainers;
    }

    get hiddenAccordions() {
        return this._hiddenAccordions;
    }

    get hiddenBlocks() {
        return this._hiddenBlocks;
    }

    get contextModelState() {
        return this._contextModelService.state;
    }

    get contextModelCurrentState() {
        return this._contextModelService.currentState;
    }

    loadConfiguration(configuration: FrontendConfiguration): void {
        this.reset();

        if (!configuration.compiledFlowBlocks) {
            this.invalidConfiguration.set(true);
            console.error(
                'QuestionFlowService: configuration is missing `compiledFlowBlocks` - it must be re-exported from the Flow Editor.'
            );
        }

        try {
            this._flowData = configuration.flow;
            this._compiledFlowBlocks = configuration.compiledFlowBlocks;
            this._blocks = this.mergeBlocksWithCompiledFlowBlocks(configuration.flow, configuration.compiledFlowBlocks);

            this._firstQuestionBlock = this._blocks.find(block => block.id === this._flowData?.startingPoint.blockId)!;
            if (!this._firstQuestionBlock) {
                this.invalidConfiguration.set(true);
                console.error(
                    `QuestionFlowService: startingPoint.blockId (${this._flowData?.startingPoint.blockId}) was not found in merged blocks.`
                );
                return;
            }
            this.initQuestionsPath();
            this.startFlow();
            this.initializeValueConverterDefaults();
            this.stabilizeQuestionPath();
            this.recalculateSlotsDisabledState();
        } catch (error) {
            console.error('Error while loading configuration:', error);
        }
    }

    /**
     * Merge each flow block with its matching {@link CompiledFlowBlock}.
     *
     * The compiled block is keyed by `flow.blocks[].id` (a.k.a. `flowBlockId`) and contains the final
     * compiled template, validations, decision paths and parameters. The result is a unified `Block`
     * structure so that the rest of the service can keep using the legacy shape without branching.
     */
    private mergeBlocksWithCompiledFlowBlocks(
        flow: ApplicationFlow,
        compiledFlowBlocks: { [flowBlockId: string]: CompiledFlowBlock }
    ): Block[] {
        return flow.blocks.map(block => {
            const compiled = compiledFlowBlocks[block.id];
            return {
                ...block,
                ...(compiled as unknown as object),
                // Keep flow identity/relations from flow.blocks (compiled.id is definition id).
                id: block.id,
                parentId: block.parentId,
                childIds: block.childIds,
                inputPaths: block.inputPaths,
                outputPaths: block.outputPaths,
                triggerIds: block.triggerIds,
                order: block.order,
                definitionId: block.definitionId,
                slots: block.slots
            } as Block;
        });
    }

    startFlow(): void {
        if (!this._flowData || !this.contextModelState) {
            console.error('Flow data or project state is missing');
            return;
        }

        this._decisionBlocks = this._blocks.filter(block => block.blockType === BlockType.DecisionBlock);

        // Hide all question blocks if they depend on decision blocks
        this._decisionBlocks.forEach(block => {
            block.outputPaths?.forEach(id => {
                this.setDecisionBlockPathVisibility(id, block.parentId!, true);
            });
        });

        const firstSectionBlock = this._blocks.find(block => block.id === this._flowData?.startingPoint.sectionId);
        const firstSubSectionBlock = this._blocks.find(block => block.id === this._flowData?.startingPoint.subsectionId);

        if (!firstSectionBlock || !firstSubSectionBlock) {
            this.invalidConfiguration.set(true);
            console.error(
                `QuestionFlowService: startFlow could not resolve starting point section/subsection (${this._flowData?.startingPoint.sectionId}, ${this._flowData?.startingPoint.subsectionId}).`
            );
            return;
        }

        this.setCurrentContainerBlockId(firstSectionBlock.id);
        this.setCurrentAccordionBlockId(firstSubSectionBlock.id);
        this._pathSession = new DefaultSession();
        this._contextModelService.session = this._pathSession;

        this._accordionBlocksIdsFlow.push(firstSubSectionBlock.id);
    }

    getContainerBlocks(): Block[] {
        const containerIds = Array.from(
            new Set(
                this._questionsPath
                    .filter(Boolean)
                    .map(q => this._blocks.find(b => b.id === q.parentId)?.parentId)
                    .filter(id => !!id)
            )
        );

        const containers = uniqueById(containerIds.map(id => this._blocks.find(b => b.id === id)).filter(Boolean));

        return containers;
    }

    getAccordionBlocks(containerBlock: Block): Block[] {
        const accordions = this._questionsPath
            .map(q => this._blocks.find(b => b.id === q.parentId))
            .filter(b => !!b && b.parentId === containerBlock.id);

        return uniqueById(accordions);
    }

    getQuestionBlocks(accordionBlock: Block): Block[] {
        this.evaluateDecisionBlocks(accordionBlock.id);
        const blocks: Block[] = uniqueById(this._questionsPath.filter(b => b.parentId === accordionBlock.id));
        return blocks;
    }

    onContainer(block: Block) {
        const currentContainerBlockId = this.currentContainerBlockId;
        // check if target container is not the current one
        if (currentContainerBlockId === block.id) {
            return;
        }

        const accordionBlocks = this.getAccordionBlocks(block);
        const firstAccordionBlock = accordionBlocks?.[0];

        if (!firstAccordionBlock) {
            return;
        }

        this.onAccordion(firstAccordionBlock);
    }

    onAccordion(accordionBlock: Block) {
        const currentAccordionBlockId = this.currentAccordionBlockId;
        // check if target block is in the flow or it is the current block
        if (!this._accordionBlocksIdsFlow.includes(accordionBlock.id) || currentAccordionBlockId === accordionBlock.id) {
            return;
        }

        const untilIds = this.getAccordionBlocksIdsFlowUntil(accordionBlock.id);
        const afterIds = this.getAccordionBlocksIdsFlowAfter(accordionBlock.id);
        const blocksAfterABlock = afterIds.map(id => this._blocks.find(b => b.id === id)!);
        blocksAfterABlock.forEach(block => {
            this.resetDecisionBlocksOutput(block);
        });

        this._accordionBlocksIdsFlow = untilIds;

        const nextAccordionBlockId = this._accordionBlocksIdsFlow[this._accordionBlocksIdsFlow.length - 1];
        const nextAccordionBlock = this._blocks.find(b => b.id === nextAccordionBlockId);
        const nextContainerBlockId = nextAccordionBlock?.parentId!;

        this.setCurrentAccordionBlockId(nextAccordionBlockId);
        this.setCurrentContainerBlockId(nextContainerBlockId);

        this.recalculateContainersOnBack(nextContainerBlockId, nextAccordionBlockId);
    }

    onBack(accordionBlock: Block) {
        this.resetDecisionBlocksOutput(accordionBlock);

        this._accordionBlocksIdsFlow.pop();

        const nextAccordionBlockId = this._accordionBlocksIdsFlow[this._accordionBlocksIdsFlow.length - 1];
        const nextAccordionBlock = this._blocks.find(b => b.id === nextAccordionBlockId);
        const nextContainerBlockId = nextAccordionBlock?.parentId!;

        this.setCurrentAccordionBlockId(nextAccordionBlockId);
        this.setCurrentContainerBlockId(nextContainerBlockId);

        this.recalculateContainersOnBack(nextContainerBlockId, nextAccordionBlockId);
    }

    onNext(accordionBlock: Block) {
        const currentContainerBlockId = this.currentContainerBlockId;
        const currentAccordionBlockId = this.currentAccordionBlockId;

        this.initQuestionsPath();

        const questionBlocks = this.getQuestionBlocks(accordionBlock);
        const lastBlock = questionBlocks[questionBlocks.length - 1];
        if (!lastBlock) {
            return;
        }

        if (lastBlock.blockType === BlockType.QuestionBlock) {
            const decisionBlock = this._decisionBlocks.find(b => b.id === lastBlock.outputPaths?.[0]);
            if (decisionBlock) {
                this.processDecisionBlock(decisionBlock);
            }
        }

        const nextBlockId =
            lastBlock.blockType === BlockType.DecisionBlock ? this._decisionBlocksOutput[lastBlock.id] : lastBlock.outputPaths?.[0];
        let nextBlock = this._blocks.find(b => b.id === nextBlockId);
        if (!nextBlock) {
            return;
        }

        nextBlock = this.searchForQuestionBlock(nextBlock);
        if (!nextBlock) {
            return;
        }

        if (lastBlock.blockType === BlockType.QuestionBlock && nextBlock.blockType === BlockType.QuestionBlock) {
            this.hiddenBlocks[nextBlock.id] = false;
        }

        const nextAccordionBlock = this._blocks.find(b => b.id === nextBlock!.parentId);
        const nextContainerBlock = this._blocks.find(b => b.id === nextAccordionBlock?.parentId);

        const nextContainerBlockId = nextContainerBlock!.id;
        const nextAccordionBlockId = nextAccordionBlock!.id;

        this.setCurrentAccordionBlockId(nextAccordionBlockId);
        this.setCurrentContainerBlockId(nextContainerBlockId);

        this._accordionBlocksIdsFlow.push(nextAccordionBlockId);

        this.recalculateContainersOnNext(currentContainerBlockId, nextContainerBlockId, currentAccordionBlockId, nextAccordionBlockId);
    }

    onResult(): void {
        console.log('Result:', this.contextModelCurrentState);
    }

    onQuestionValueChange(accordionBlock: Block, questionBlock: Block, value: any) {
        const previousCleanupSnapshot = this._stateCleanupService.createSnapshot(this._questionsPath);

        // START - recalculate the flow
        this.resetQuestionPath();
        this.calculateQuestionPath(this._firstQuestionBlock);
        // END - recalculate the flow

        questionBlock.outputPaths?.forEach(pathId => {
            const block = this._blocks.find(b => b.id === pathId);
            this.processDecisionBlock(block!);
        });

        const currentCleanupSnapshot = this._stateCleanupService.createSnapshot(
            this._questionsPath,
            slot => !this.isSlotEffectivelyInactive(slot)
        );

        const wasCleaned = this._stateCleanupService.trimStateOnPathReduction(
            previousCleanupSnapshot,
            currentCleanupSnapshot,
            this.contextModelCurrentState,
            this._pathResolver,
            this._pathSession
        );
        if (wasCleaned) {
            this.contextStateVersion.update(version => version + 1);
        }

        // Drop pending values for slots whose owning block left the active path
        // (e.g. a heat source removed via its counter). Otherwise re-adding the block
        // would resurface the stale pending value instead of the converter default.
        this.pruneStalePendingInactiveValues();

        // After path recalculation, newly visible slots with valueConverters
        // (e.g. typeOfWindow, typeOfSunProtection) need their defaults applied
        // from the initial context model state (standardTypeOfWindow, standardTypeOfSunProtection).
        // Only process blocks AFTER the edited question (linear forward-only refresh).
        this.seedSessionFromExistingState();
        this.initializeValueConverterDefaultsAfter(questionBlock);
        this.recalculateSlotsDisabledState();
    }

    /**
     * Returns the value exposed to the UI for a slot (its "displayed" value).
     *
     * Unlike {@link getNestedValueFromStates}, which performs the raw nested
     * lookup, this method reconciles the three representations of a slot's value:
     * - the stored value read from the context model state,
     * - a pending value entered while the slot was inactive, and
     * - the value produced by `valueConverter.fromParameter`.
     *
     * Each converter owns how it handles an empty source value (e.g. the heat
     * source `amount` converter defaults to `1`, while others can return an
     * empty value), so no `isInactive`-based coupling is applied here.
     */
    getDisplayedSlotValue(slot: TemplateSlot): any {
        if (slot.valueConverter) {
            const sourceValue = this.getNestedValueFromStates(slot, this._pathSession);
            // A value the user entered while the slot was inactive takes precedence
            // over the converter output until it is flushed into the state.
            if (sourceValue === undefined && this._pendingInactiveSlotValues.has(slot)) {
                return this._pendingInactiveSlotValues.get(slot);
            }

            return this._contextModelService.processJsonLogic(slot.valueConverter.fromParameter, {
                context: { value: sourceValue, globalContext: this.contextModelCurrentState }
            });
        }

        const storedValue = this.getNestedValueFromStates(slot, this._pathSession);
        if (storedValue === undefined && this._pendingInactiveSlotValues.has(slot)) {
            return this._pendingInactiveSlotValues.get(slot);
        }
        return storedValue;
    }

    getCombinedNestedValue(slots: TemplateSlot[] | undefined): { [key: string]: TemplateSlot } | undefined {
        if (!slots) return;

        let combinedValue: any = {};
        slots.forEach(slot => {
            combinedValue[slot.id] = this.getDisplayedSlotValue(slot);
        });
        return combinedValue;
    }

    private initializeValueConverterDefaults(): void {
        this._questionsPath
            .filter(block => block.blockType === BlockType.QuestionBlock)
            .flatMap(block => block.template?.slots || [])
            .filter(slot => slot.valueConverter && !this.isSlotInactive(slot))
            .forEach(slot => {
                const compiledPath = this.resolveSlotCompiledPath(slot);
                const storedValue = this.getNestedValueFromStates(slot, this._pathSession);
                const displayValue = this._contextModelService.processJsonLogic(slot.valueConverter!.fromParameter, {
                    context: { value: storedValue, globalContext: this.contextModelCurrentState }
                });
                this.setNestedValue(slot, compiledPath, displayValue, this._pathSession);
            });
    }

    /**
     * Same as {@link initializeValueConverterDefaults} but only processes blocks
     * that appear AFTER the given `afterBlock` in the linear question path.
     * This enforces the "forward-only refresh" requirement: editing a slot must
     * never trigger value converter recalculation for preceding blocks.
     */
    private initializeValueConverterDefaultsAfter(afterBlock: Block): void {
        const blockIndex = this._questionsPath.indexOf(afterBlock);
        const startIndex = blockIndex === -1 ? 0 : blockIndex + 1;

        this._questionsPath
            .slice(startIndex)
            .filter(block => block.blockType === BlockType.QuestionBlock)
            .flatMap(block => block.template?.slots || [])
            .filter(slot => slot.valueConverter && !this.isSlotInactive(slot))
            .forEach(slot => {
                const compiledPath = this.resolveSlotCompiledPath(slot);
                const storedValue = this.getNestedValueFromStates(slot, this._pathSession);
                const displayValue = this._contextModelService.processJsonLogic(slot.valueConverter!.fromParameter, {
                    context: { value: storedValue, globalContext: this.contextModelCurrentState }
                });
                this.setNestedValue(slot, compiledPath, displayValue, this._pathSession);
            });
    }

    /**
     * Drops pending inactive-slot values whose owning block is no longer part of
     * the active question path (e.g. a heat source removed via its counter). Without
     * this, re-adding the block would resurface the stale pending value instead of
     * the slot's converter default.
     */
    private pruneStalePendingInactiveValues(): void {
        if (this._pendingInactiveSlotValues.size === 0) {
            return;
        }

        const activeSlots = new Set<TemplateSlot>(
            this._questionsPath.filter(block => block.blockType === BlockType.QuestionBlock).flatMap(block => block.template?.slots ?? [])
        );

        for (const slot of Array.from(this._pendingInactiveSlotValues.keys())) {
            if (!activeSlots.has(slot)) {
                this._pendingInactiveSlotValues.delete(slot);
            }
        }
    }

    private isSlotInactive(slot: TemplateSlot): boolean {
        if (slot.isInactive === undefined) {
            return false;
        }
        return !!this._contextModelService.processJsonLogic(slot.isInactive);
    }

    /**
     * Returns `true` when a slot is effectively not contributing data.
     * A slot is considered inactive when either `isInactive` or `isHidden`
     * evaluates to `true` (e.g. hidden by a controlling checkbox).
     */
    private isSlotEffectivelyInactive(slot: TemplateSlot): boolean {
        if (this.isSlotInactive(slot)) {
            return true;
        }
        if (slot.isHidden === undefined || slot.isHidden === false) {
            return false;
        }
        return !!this._contextModelService.processJsonLogic(slot.isHidden);
    }

    /**
     * After {@link initializeValueConverterDefaults} populates derived values
     * (e.g. `wallsCount` from `wallIds.length`), decision blocks may now
     * evaluate differently, revealing new question blocks. Those new blocks
     * can carry their own value converters that unlock further blocks
     * (e.g. `windowsCount` per wall → window controls).
     *
     * This method repeats `recalculate path → seed session → init value converter defaults`
     * until the set of active blocks no longer changes.
     */
    private stabilizeQuestionPath(): void {
        const MAX_ITERATIONS = 10;
        for (let i = 0; i < MAX_ITERATIONS; i++) {
            const previousBlockIds = this._questionsPath.map(b => b.id).join(',');
            this.resetQuestionPath();
            this._questionsPath.push(this._firstQuestionBlock);
            this.calculateQuestionPath(this._firstQuestionBlock);
            this.seedSessionFromExistingState();
            this.initializeValueConverterDefaults();
            const currentBlockIds = this._questionsPath.map(b => b.id).join(',');
            if (currentBlockIds === previousBlockIds) {
                break;
            }
        }
    }

    /**
     * Pre-populate the session with ACTION/ID mappings from data already
     * present in the initial context model state.
     *
     * Without this, the first `getValue` / `setValue` for paths with
     * `{ID(category):targetPath}` tokens generates a fresh UUID instead
     * of reusing the existing entity ID (e.g. `ckYqK0kz8Wnl` for a wall).
     * That makes the PathResolver look up a non-existent key and return
     * `undefined`, so controls backed by ACTION/ID paths appear empty.
     *
     * We walk every ACTION token in every active slot's compiled path,
     * resolve its `targetTemplate` against the initial state to obtain the
     * real ID, and register it in the session via `session.setId()`.
     */
    private seedSessionFromExistingState(): void {
        if (!this._pathSession) {
            return;
        }

        const state = this.contextModelCurrentState ?? this.contextModelState;
        if (!state) {
            return;
        }

        this._questionsPath
            .filter(block => block.blockType === BlockType.QuestionBlock)
            .flatMap(block => block.template?.slots || [])
            .forEach(slot => {
                const compiledPath = this.resolveSlotCompiledPath(slot);
                if (!compiledPath) {
                    return;
                }
                this.seedSessionForTokens(compiledPath.tokens, compiledPath, state);
            });
    }

    /**
     * Recursively walk tokens and register existing IDs for any ACTION tokens.
     * Uses {@link resolveTokensFromState} instead of PathResolver to avoid
     * generating random UUIDs for unresolved ACTION tokens during reads.
     */
    private seedSessionForTokens(tokens: readonly Token[], compiledPath: CompiledPath, state: Record<string, any>): void {
        for (const token of tokens) {
            if (token.kind !== TokenKind.Action) {
                continue;
            }

            const { category, targetTemplate } = token as Token & { category: string; targetTemplate: Token[] };

            // Recursively seed inner ACTION tokens first (e.g. nested wall → room relation)
            this.seedSessionForTokens(targetTemplate, compiledPath, state);

            const normalizedPath = this.normalizeTargetTokens(targetTemplate, compiledPath, state);
            if (this._pathSession!.getId(category, normalizedPath) !== undefined) {
                continue;
            }

            // Read the existing entity ID by walking the state manually.
            // This avoids PathResolver.getValue which generates random UUIDs
            // for ACTION tokens that are not yet in the session.
            const existingId = this.resolveTokensFromState(targetTemplate, compiledPath, state);
            if (existingId && typeof existingId === 'string') {
                this._pathSession!.setId(category, normalizedPath, existingId);
            }
        }
    }

    /**
     * Normalize a target template into concrete NormalizedToken[] for session key lookup.
     * Mirrors the PathResolver's internal normalizeTargetPath logic.
     * Uses {@link resolveTokensFromState} for dynamic token resolution to avoid
     * PathResolver side-effects on the session.
     */
    private normalizeTargetTokens(
        targetTemplate: readonly Token[],
        compiledPath: CompiledPath,
        state: Record<string, any>
    ): NormalizedToken[] {
        const normalized: NormalizedToken[] = [];

        for (const token of targetTemplate) {
            switch (token.kind) {
                case TokenKind.Property:
                    normalized.push(createPropToken(token.name));
                    break;
                case TokenKind.Index:
                    normalized.push(createIndexToken(token.index));
                    break;
                case TokenKind.Dynamic: {
                    const dynamicSlot = compiledPath.dynamicSlots[token.slot];
                    if (dynamicSlot) {
                        const slotValue = this.resolveTokensFromState(dynamicSlot.tokens, compiledPath, state);
                        if (slotValue !== undefined) {
                            if (typeof slotValue === 'number' && token.isIndex) {
                                normalized.push(createIndexToken(slotValue));
                            } else {
                                normalized.push(createPropToken(String(slotValue)));
                            }
                        }
                    }
                    break;
                }
                case TokenKind.Action: {
                    const innerTemplate = (token as Token & { targetTemplate: Token[] }).targetTemplate;
                    const innerNormalized = this.normalizeTargetTokens(innerTemplate, compiledPath, state);
                    const innerId = this._pathSession!.getId((token as Token & { category: string }).category, innerNormalized);
                    if (innerId) {
                        normalized.push(createPropToken(innerId));
                    }
                    break;
                }
            }
        }

        return normalized;
    }

    /**
     * Walk the state object manually using compiled path tokens.
     * Unlike PathResolver.getValue, this does NOT generate random UUIDs
     * for ACTION tokens that are missing from the session. Instead it
     * reads the existing entity ID directly from the target template
     * location in the state.
     */
    private resolveTokensFromState(tokens: readonly Token[], compiledPath: CompiledPath, state: Record<string, any>): any {
        let current: any = state;

        for (const token of tokens) {
            if (current === undefined || current === null) {
                return undefined;
            }
            current = this.resolveOneToken(token, current, compiledPath, state);
        }

        return current;
    }

    private resolveOneToken(token: Token, current: any, compiledPath: CompiledPath, state: Record<string, any>): any {
        switch (token.kind) {
            case TokenKind.Property:
                return typeof current === 'object' && !Array.isArray(current) ? current[token.name] : undefined;
            case TokenKind.Index:
                return Array.isArray(current) ? current[token.index] : undefined;
            case TokenKind.Dynamic:
                return this.resolveDynamicFromState(token, current, compiledPath, state);
            case TokenKind.Action:
                return this.resolveActionFromState(
                    token as Token & { category: string; targetTemplate: Token[] },
                    current,
                    compiledPath,
                    state
                );
            default:
                return undefined;
        }
    }

    private resolveDynamicFromState(token: Token, current: any, compiledPath: CompiledPath, state: Record<string, any>): any {
        const dynamicSlot = compiledPath.dynamicSlots[(token as Token & { slot: number }).slot];
        if (!dynamicSlot) {
            return undefined;
        }
        const slotValue = this.resolveTokensFromState(dynamicSlot.tokens, compiledPath, state);
        if (typeof slotValue === 'string') {
            return typeof current === 'object' && !Array.isArray(current) ? current[slotValue] : undefined;
        }
        if (typeof slotValue === 'number') {
            return Array.isArray(current) ? current[slotValue] : undefined;
        }
        return undefined;
    }

    private resolveActionFromState(
        token: Token & { category: string; targetTemplate: Token[] },
        current: any,
        compiledPath: CompiledPath,
        state: Record<string, any>
    ): any {
        const entityId = this.resolveTokensFromState(token.targetTemplate, compiledPath, state);
        if (typeof entityId !== 'string') {
            return undefined;
        }
        if (Array.isArray(current)) {
            return current.includes(entityId) ? entityId : undefined;
        }
        if (typeof current === 'object') {
            return current[entityId];
        }
        return undefined;
    }

    private getNestedValueFromStates(slot: TemplateSlot, sessionInstance: Session | undefined): any {
        const compiledPath = this.resolveSlotCompiledPath(slot);

        // Ensure ACTION token IDs are seeded from the state before PathResolver
        // accesses the session. Without this, PathResolver generates random UUIDs
        // for ACTION tokens that are not yet cached, making entity lookups fail.
        if (sessionInstance && compiledPath?.tokens?.some((t: Token) => t.kind === TokenKind.Action)) {
            const seedState = this.contextModelCurrentState ?? this.contextModelState;
            if (seedState) {
                this.seedSessionForTokens(compiledPath.tokens, compiledPath, seedState);
            }
        }

        const valueFromCurrentState = this._pathResolver.getValue(compiledPath, this.contextModelCurrentState, sessionInstance);
        if (valueFromCurrentState !== undefined) {
            return valueFromCurrentState;
        }

        // If the parent array exists in the current state (even if empty/short),
        // it means the indexed entry was explicitly removed (e.g. by cleanup trimming).
        // Do NOT fall back to the initial state in that case.
        if (this.isIndexedPathWithTrimmedParent(compiledPath, sessionInstance)) {
            return undefined;
        }

        const valueFromInitialState = this._pathResolver.getValue(compiledPath, this.contextModelState, sessionInstance);
        return valueFromInitialState;
    }

    /**
     * Returns `true` when the compiled path targets an indexed array entry
     * and the parent array already exists in the current state (even if it's
     * empty or shorter than the requested index).
     *
     * This detects a "trimmed" array: the entry was explicitly removed by
     * the cleanup service, so we must NOT fall back to the initial state.
     *
     * Also recursively checks ACTION tokens' targetTemplates for nested INDEX
     * tokens (e.g. `{ID(wall):...wallIds.[0]}` — the INDEX is inside the
     * ACTION's target, not a direct child of the top-level token list).
     */
    private isIndexedPathWithTrimmedParent(compiledPath: CompiledPath, sessionInstance: Session | undefined): boolean {
        const tokens = compiledPath?.tokens;
        if (!tokens?.length) {
            return false;
        }

        // Check top-level INDEX tokens.
        if (this.hasIndexTokenWithTrimmedParent(tokens, compiledPath, sessionInstance)) {
            return true;
        }

        // Check ACTION tokens' targetTemplates recursively for nested INDEX tokens.
        return this.hasActionTokenWithTrimmedArray(tokens, compiledPath, sessionInstance);
    }

    /**
     * Returns `true` when `tokens` contains at least one INDEX token whose
     * parent array exists in the current state (i.e. was trimmed/cleared).
     */
    private hasIndexTokenWithTrimmedParent(
        tokens: readonly Token[],
        compiledPath: CompiledPath,
        sessionInstance: Session | undefined
    ): boolean {
        let lastIndexPos = -1;
        for (let i = tokens.length - 1; i >= 0; i--) {
            if (tokens[i].kind === TokenKind.Index) {
                lastIndexPos = i;
                break;
            }
        }

        if (lastIndexPos <= 0) {
            return false;
        }

        const parentPath = {
            originalPath: '',
            tokens: tokens.slice(0, lastIndexPos),
            dynamicSlots: compiledPath.dynamicSlots
        } as CompiledPath;

        const parentValue = this._pathResolver.getValue(parentPath, this.contextModelCurrentState, sessionInstance);
        return Array.isArray(parentValue);
    }

    /**
     * Recursively walks ACTION tokens and checks whether any inner
     * targetTemplate contains an INDEX token whose parent array has
     * been trimmed/cleared in the current state.
     */
    private hasActionTokenWithTrimmedArray(
        tokens: readonly Token[],
        compiledPath: CompiledPath,
        sessionInstance: Session | undefined
    ): boolean {
        for (const token of tokens) {
            if (token.kind !== TokenKind.Action) {
                continue;
            }

            const actionToken = token as Token & { targetTemplate: Token[] };
            if (!actionToken.targetTemplate?.length) {
                continue;
            }

            if (this.hasIndexTokenWithTrimmedParent(actionToken.targetTemplate, compiledPath, sessionInstance)) {
                return true;
            }

            // Recurse into nested ACTION tokens within the targetTemplate.
            if (this.hasActionTokenWithTrimmedArray(actionToken.targetTemplate, compiledPath, sessionInstance)) {
                return true;
            }
        }
        return false;
    }

    setNestedValue(slot: TemplateSlot, compiledPath: CompiledPath, value: any, sessionInstance: Session | undefined): void {
        if (slot.valueConverter) {
            const convertedValue = this._contextModelService.processJsonLogic(slot.valueConverter.toParameter, {
                context: { value, globalContext: this.contextModelCurrentState }
            });
            this.setPathResolverValue(compiledPath, sessionInstance, convertedValue);
            const wasUpdated = this._stateCleanupService.ensureRelationReferenceConsistency(
                slot,
                this.contextModelCurrentState,
                this._pathResolver,
                this._pathSession
            );
            if (wasUpdated) {
                this.contextStateVersion.update(version => version + 1);
            }
            return;
        }

        this.setPathResolverValue(compiledPath, sessionInstance, value);
        const wasUpdated = this._stateCleanupService.ensureRelationReferenceConsistency(
            slot,
            this.contextModelCurrentState,
            this._pathResolver,
            this._pathSession
        );
        if (wasUpdated) {
            this.contextStateVersion.update(version => version + 1);
        }
    }

    /**
     * Read the compiled path directly from the slot object.
     *
     * Each `TemplateSlot` inside a `CompiledFlowBlock` already carries its own
     * `compiledOutputPath` / `compiledOutputParameter` with the correct array index.
     * Using this avoids the ambiguity of {@link getCompiledPathForSlot} which searches
     * all compiled blocks by slot ID and can return the wrong path when the same block
     * definition is instantiated multiple times.
     */
    private resolveSlotCompiledPath(slot: TemplateSlot): CompiledPath {
        const compiledPath = slot.compiledOutputPath ?? slot.compiledOutputParameter;
        if (compiledPath) {
            return compiledPath;
        }
        // Fallback for slots that don't carry an embedded compiled path.
        return this.getCompiledPathForSlot(slot.id);
    }

    /**
     * Resolve the compiled path for a slot from the compiled flow blocks.
     *
     * The new flow exports the compiled path on `CompiledFlowBlock.template.slots[].compiledOutputPath`
     * (the Flow Editor JSON currently calls this property `compiledOutputParameter` - both names
     * are accepted here for forward / backward compatibility).
     */
    private getCompiledPathForSlot(slotId: string): CompiledPath {
        if (!this._compiledFlowBlocks) {
            throw new Error(`compiledFlowBlocks are not loaded - cannot resolve compiled path for slot ${slotId}`);
        }

        for (const compiledBlock of Object.values(this._compiledFlowBlocks)) {
            const slot = compiledBlock.template?.slots?.find(s => s.id === slotId);
            if (slot) {
                const compiledPath = slot.compiledOutputPath ?? slot.compiledOutputParameter;
                if (!compiledPath) {
                    console.error(
                        `Slot with id ${slotId} has no compiledOutputPath / compiledOutputParameter in compiled flow block ${compiledBlock.flowBlockId}`
                    );
                }
                return compiledPath as CompiledPath;
            }
        }

        console.error(`Slot with id ${slotId} is missing in compiledFlowBlocks`);
        return undefined as unknown as CompiledPath;
    }

    /**
     * Processes all slots from the current accordion block and sets nested values
     * for each compiled path that already exists in the contextModelCurrentState.
     */
    recalculateContextModel(questionBlocks: Block[], editedSlot: TemplateSlot, value: any): void {
        // When the edited slot is inactive, its value must not be written to the
        // context model state. Keep the value pending so it can be flushed later, once the
        // controlling flexi-logic dependency turns the slot active again.
        if (this.isSlotInactive(editedSlot)) {
            this._pendingInactiveSlotValues.set(editedSlot, value);
            return;
        }

        // An active slot must not materialize a brand-new indexed array entry while a
        // sibling that gates that entry (via `isInactive`) would still be inactive.
        // Example: entering `connectedLoad` before selecting the heat source `type` must
        // not create `heatSources[i]`. The value is kept pending until the controlling slot
        // creates the entry, then flushed by persistPendingActiveSlotValues().
        if (this.shouldDeferIndexedEntryCreation(questionBlocks, editedSlot, value)) {
            this._pendingInactiveSlotValues.set(editedSlot, value);
            return;
        }

        // The edited slot is active - drop any stale pending value for it so the
        // freshly provided value is the single source of truth.
        this._pendingInactiveSlotValues.delete(editedSlot);

        // Clone the state before any modifications so that each slot reads
        // its original stored value, independent of processing order.
        const contextModelStateBeforeUpdate = FlowUtils.cloneDeep(this.contextModelCurrentState);

        // Process the edited slot first so that array holes are filled before
        // sibling slots at other indices are evaluated.
        const editedSlotCompiledPath = this.resolveSlotCompiledPath(editedSlot);
        this.setNestedValue(editedSlot, editedSlotCompiledPath, value, this._pathSession);

        questionBlocks.forEach(questionBlock => {
            if (!questionBlock.template?.slots) {
                return;
            }
            questionBlock.template.slots.forEach(slot => {
                // Use reference equality — slot IDs are not unique across flow blocks
                // that share the same block definition.
                if (slot === editedSlot) {
                    return;
                }
                const slotCompiledPath = this.resolveSlotCompiledPath(slot);
                const existingValue = this._pathResolver.getValue(slotCompiledPath, this.contextModelCurrentState, this._pathSession);
                if (existingValue === undefined) {
                    return;
                }
                if (slot.valueConverter) {
                    const storedValue = this._pathResolver.getValue(slotCompiledPath, contextModelStateBeforeUpdate, this._pathSession);
                    if (storedValue === undefined) {
                        // Slot path didn't exist before but was created (e.g. array hole filling).
                        // Apply valueConverter to produce a proper default from the placeholder value.
                        const displayValue = this._contextModelService.processJsonLogic(slot.valueConverter.fromParameter, {
                            context: {
                                ...this.contextModelCurrentState,
                                value: existingValue,
                                globalContext: this.contextModelCurrentState
                            }
                        }) as string | number;
                        const convertedValue = this._contextModelService.processJsonLogic(slot.valueConverter.toParameter, {
                            context: { value: displayValue }
                        });
                        this.setPathResolverValue(slotCompiledPath, this._pathSession, convertedValue);
                        return;
                    }
                    // Recalculate: use fromParameter with pre-update state to recover the original user input,
                    // then toParameter to derive the new stored value from updated context.
                    const retrievedValue = this._contextModelService.processJsonLogic(slot.valueConverter.fromParameter, {
                        context: { ...contextModelStateBeforeUpdate, value: storedValue, globalContext: contextModelStateBeforeUpdate }
                    }) as string | number;
                    const convertedValue = this._contextModelService.processJsonLogic(slot.valueConverter.toParameter, {
                        context: { value: retrievedValue }
                    });
                    this.setPathResolverValue(slotCompiledPath, this._pathSession, convertedValue);
                    return;
                }
                this.setPathResolverValue(slotCompiledPath, this._pathSession, existingValue);
            });
        });

        // Writing the edited slot may have satisfied the flexi-logic dependency of
        // other slots (e.g. selecting a heat source `type` activates its `amount`).
        // Persist the value shown for those active slots into the state.
        this.persistPendingActiveSlotValues(questionBlocks);
    }

    /**
     * Persists the displayed value of currently active slots that still hold a
     * pending value not yet written to the context model state.
     *
     * This does not track an explicit inactive -> active transition: a slot's
     * value is only displayed (never stored) while it is inactive, so once it is
     * active this pass writes any still-pending value into the state. A slot is
     * considered to have a pending value when either:
     * - the user entered a value while it was inactive (pending), or
     * - it is an empty inactive-capable converter slot whose converter default
     *   should be stored (e.g. the `amount = 1` default of a freshly added heat
     *   source).
     * Still-inactive slots keep their displayed value for a later pass.
     */
    private persistPendingActiveSlotValues(questionBlocks: Block[]): void {
        questionBlocks.forEach(questionBlock => {
            questionBlock.template?.slots?.forEach(slot => {
                if (this.isSlotInactive(slot)) {
                    return;
                }
                const hasPendingValue = this._pendingInactiveSlotValues.has(slot);
                const storedValue = this.getNestedValueFromStates(slot, this._pathSession);
                const needsConverterDefault = storedValue === undefined && !!slot.valueConverter && slot.isInactive !== undefined;
                if (!hasPendingValue && !needsConverterDefault) {
                    return;
                }
                const valueToPersist = this.getDisplayedSlotValue(slot);
                this._pendingInactiveSlotValues.delete(slot);
                if (valueToPersist !== undefined && valueToPersist !== null) {
                    const compiledPath = this.resolveSlotCompiledPath(slot);
                    this.setNestedValue(slot, compiledPath, valueToPersist, this._pathSession);
                }
            });
        });
    }

    /**
     * Returns `true` when writing the edited slot would create a brand-new indexed
     * array entry (e.g. `heatSources[i]`) while a sibling slot that gates that same
     * entry via its `isInactive` condition would still be inactive afterwards.
     *
     * This keeps entry creation driven by the controlling slot only: e.g. a heat
     * source object must appear once its `type` is chosen (which activates `amount`),
     * not when an ungated field such as `connectedLoad` is filled in first. The value
     * is kept pending like an inactive slot and flushed by
     * {@link persistPendingActiveSlotValues} once the entry exists.
     */
    private shouldDeferIndexedEntryCreation(questionBlocks: Block[], editedSlot: TemplateSlot, value: any): boolean {
        const compiledPath = this.resolveSlotCompiledPath(editedSlot);
        const entryPath = this.resolveIndexedEntryPath(compiledPath);
        if (!entryPath) {
            return false;
        }

        // Only relevant while the entry is being created (it does not exist yet).
        const entryExists = this._pathResolver.getValue(entryPath, this.contextModelCurrentState, this._pathSession) !== undefined;
        if (entryExists) {
            return false;
        }

        // Apply the edit on a throwaway copy of the state, then check whether any
        // sibling targeting the same entry would remain inactive.
        const hypotheticalState = FlowUtils.cloneDeep(this.contextModelCurrentState);
        const valueToWrite = editedSlot.valueConverter
            ? this._contextModelService.processJsonLogic(editedSlot.valueConverter.toParameter, {
                  context: { value, globalContext: this.contextModelCurrentState }
              })
            : value;
        this._pathResolver.setValue(compiledPath, hypotheticalState, this._pathSession, valueToWrite);

        return questionBlocks
            .flatMap(block => block.template?.slots ?? [])
            .some(
                slot =>
                    slot !== editedSlot &&
                    slot.isInactive !== undefined &&
                    this.slotTargetsIndexedEntry(slot, entryPath) &&
                    !!FlexiLogic.calculate(slot.isInactive, hypotheticalState, this._pathSession)
            );
    }

    /**
     * Returns the compiled path pointing at the indexed array ELEMENT of `compiledPath`
     * (tokens up to and including the last INDEX token), or `undefined` when the path
     * does not target a plain indexed array entry.
     */
    private resolveIndexedEntryPath(compiledPath: CompiledPath | undefined): CompiledPath | undefined {
        const tokens = compiledPath?.tokens;
        if (!tokens?.length) {
            return undefined;
        }

        let lastIndexPos = -1;
        for (let i = tokens.length - 1; i >= 0; i--) {
            if (tokens[i].kind === TokenKind.Index) {
                lastIndexPos = i;
                break;
            }
        }

        if (lastIndexPos < 0) {
            return undefined;
        }

        return {
            originalPath: '',
            tokens: tokens.slice(0, lastIndexPos + 1),
            dynamicSlots: compiledPath!.dynamicSlots
        } as CompiledPath;
    }

    /**
     * Returns `true` when `slot` writes into the same indexed array element as `entryPath`.
     */
    private slotTargetsIndexedEntry(slot: TemplateSlot, entryPath: CompiledPath): boolean {
        const slotEntryPath = this.resolveIndexedEntryPath(this.resolveSlotCompiledPath(slot));
        if (!slotEntryPath) {
            return false;
        }

        return JSON.stringify(slotEntryPath.tokens) === JSON.stringify(entryPath.tokens);
    }

    private setPathResolverValue(compiledPath: CompiledPath, sessionInstance: Session | undefined, value: unknown): void {
        this._pathResolver.setValue(compiledPath, this.contextModelCurrentState, sessionInstance, value);
        this.fillArrayNullHoles(compiledPath, sessionInstance);
        this.contextStateVersion.update(version => version + 1);
    }

    /**
     * After PathResolver.setValue creates an array entry at a given index,
     * preceding positions that were not yet populated are left as `null`.
     * This replaces those null holes with empty objects `{}` so that
     * downstream consumers always find a valid object at every index.
     */
    private fillArrayNullHoles(compiledPath: CompiledPath, sessionInstance: Session | undefined): void {
        const tokens = compiledPath?.tokens;
        if (!tokens?.length) {
            return;
        }

        // Find the last INDEX token in the path.
        let lastIndexPos = -1;
        for (let i = tokens.length - 1; i >= 0; i--) {
            if (tokens[i].kind === TokenKind.Index) {
                lastIndexPos = i;
                break;
            }
        }

        if (lastIndexPos <= 0) {
            return;
        }

        const parentPath = {
            originalPath: '',
            tokens: tokens.slice(0, lastIndexPos),
            dynamicSlots: compiledPath.dynamicSlots
        } as CompiledPath;

        const parentArray = this._pathResolver.getValue(parentPath, this.contextModelCurrentState, sessionInstance);
        if (!Array.isArray(parentArray)) {
            return;
        }

        for (let i = 0; i < parentArray.length; i++) {
            if (parentArray[i] == null) {
                parentArray[i] = {};
            }
        }
    }

    private searchForQuestionBlock(block: Block): Block | undefined {
        if (block?.blockType === BlockType.QuestionBlock) {
            return block;
        }

        if (block?.blockType === BlockType.DecisionBlock) {
            const nextBlockId = this._decisionBlocksOutput[block.id];
            const nextBlock = this._blocks.find(b => b.id === nextBlockId);
            return this.searchForQuestionBlock(nextBlock!);
        }

        return undefined;
    }

    private getOutputIdAfterDecisionBlockBasedOnDefaultValues(block: Block): string | undefined {
        let path: BlockDecisionPath | undefined;
        for (const decisionPath of block.decisionPaths!) {
            if (decisionPath.logicalCondition) {
                const result = FlexiLogic.calculate(
                    decisionPath.logicalCondition,
                    this.getCalculatedContextModelCurrentStateWithDefaults(block.inputParameters),
                    this._pathSession
                );
                if (result) {
                    const idx = block.decisionPaths?.indexOf(decisionPath);
                    return block.outputPaths?.[idx!];
                }
            } else {
                path = decisionPath;
            }
        }
        if (path) {
            const idx = block.decisionPaths?.indexOf(path);
            return block.outputPaths?.[idx!];
        }
        return undefined;
    }

    /**
     * Returns the context model current state enriched with project defaults, for use during
     * decision block path evaluation. Does **not** create or persist a new state — it either
     * returns the existing {@link contextModelCurrentState} directly (when no `inputParameters`
     * are provided, or when all referenced parameters already have values), or returns a shallow
     * clone of it with missing parameter values back-filled from `project.defaults`.
     *
     * This ensures decision block conditions can always evaluate against a complete data set,
     * even when the user has not yet explicitly filled in some parameters.
     */
    private getCalculatedContextModelCurrentStateWithDefaults(inputParameters?: BlockParameter[]): ContextModelState {
        if (!inputParameters?.length) {
            return this.contextModelCurrentState;
        }

        const defaults = this.contextModelCurrentState?.project?.defaults as Record<string, any>;

        if (!defaults) {
            return this.contextModelCurrentState;
        }

        const defaultsByKeys = getDefaultsByKeys(
            defaults,
            inputParameters.map(item => item.parameter.slice('project.'.length))
        );

        if (Object.keys(defaultsByKeys).length === 0) {
            return this.contextModelCurrentState;
        }

        const stateForEvaluation = FlowUtils.cloneDeep(this.contextModelCurrentState);

        Object.entries(defaultsByKeys).forEach(([path, defaultValue]) => {
            const valueInOriginal = getNestedValue(this.contextModelCurrentState?.project, path);

            if (valueInOriginal === undefined) {
                setNestedValue(stateForEvaluation.project, path, defaultValue);
            }
        });

        return stateForEvaluation;
    }

    private processDecisionBlock(block: Block) {
        if (block?.blockType !== BlockType.DecisionBlock) {
            return;
        }

        const definitionId = this.setDecisionBlockOutput(block);
        if (!definitionId) {
            return;
        }
        const resultBlock = this._blocks.find(b => b.id === definitionId);

        // Process resultBlock if it is a decision block
        this.processDecisionBlock(resultBlock!);
    }

    private setDecisionBlockOutput(block: Block): string | undefined {
        // Assume that decision block is an only child of block
        const parentBlock = this._decisionBlocks.find(b => b.outputPaths?.includes(block.id));
        if (parentBlock && parentBlock.blockType === BlockType.DecisionBlock) {
            const resultBlockId = this.getOutputIdAfterDecisionBlockBasedOnDefaultValues(parentBlock);
            // set the result path
            if (resultBlockId) {
                this._decisionBlocksOutput[parentBlock.id] = resultBlockId;
                this._reversedDecisionBlocksOutput[resultBlockId] = parentBlock.id;
                this.setDecisionBlockPathVisibility(resultBlockId, parentBlock.parentId!, false);
            }

            // The parent's selected branch can still reach this block through a chain of
            // question blocks (convergent paths); only skip when it genuinely cannot.
            if (
                this._decisionBlocksOutput[parentBlock.id] !== block.id &&
                !this.decisionSelectedPathReachesBlock(this._decisionBlocksOutput[parentBlock.id], block.id)
            ) {
                return;
            }
        }

        const resultBlockId = this.getOutputIdAfterDecisionBlockBasedOnDefaultValues(block);
        // reset all output paths
        block.outputPaths?.forEach(pathId => {
            this.setDecisionBlockPathVisibility(pathId, block.parentId!, true);
        });
        // set the result path
        if (resultBlockId) {
            this._decisionBlocksOutput[block.id] = resultBlockId;
            this._reversedDecisionBlocksOutput[resultBlockId] = block.id;
            this.setDecisionBlockPathVisibility(resultBlockId, block.parentId!, false);
        }

        return resultBlockId;
    }

    /**
     * Returns `true` when following the selected branch of a parent decision through
     * intermediate question blocks reaches `targetBlockId` before hitting another
     * decision block. Covers convergent paths where a decision lists a downstream block
     * both directly and via a question chain on its other output.
     */
    private decisionSelectedPathReachesBlock(startBlockId: string | undefined, targetBlockId: string): boolean {
        const visited = new Set<string>();
        let currentId = startBlockId;

        while (currentId && !visited.has(currentId)) {
            if (currentId === targetBlockId) {
                return true;
            }
            visited.add(currentId);

            const current = this._blocks.find(b => b.id === currentId);
            if (!current || current.blockType === BlockType.DecisionBlock) {
                return false;
            }
            currentId = current.outputPaths?.[0];
        }

        return false;
    }

    /**
     * Set the visibility of a decision block path
     * @param blockId The id of the first block as one of the alternatives to the decision block
     * @param accordionId The id of the section containing the decision block that is the parent of the block that starts the recursion
     * @param hide Whether to hide or show the block
     * @returns
     */
    private setDecisionBlockPathVisibility(blockId: string, accordionId: string, hide: boolean): void {
        const currentBlock = this._blocks.find(block => block.id === blockId);

        /**
         * Consider the block branch from the first node (resultBlock) until we reach another decision block
         * or the next considered block is outside the currently open section
         */
        if (!currentBlock || currentBlock.blockType === BlockType.DecisionBlock || currentBlock.parentId !== accordionId) {
            return;
        }

        this.hiddenBlocks[blockId] = hide;

        currentBlock.outputPaths?.forEach(childId => {
            this.setDecisionBlockPathVisibility(childId, accordionId, hide);
        });
    }

    private nextGapIds(
        currentContainerId: string,
        nextContainerId: string,
        currentAccordionId: string | undefined,
        nextAccordionId: string,
        containers: { id: string }[],
        accordionsPerContainer: { [key: string]: { id: string }[] }
    ): { containers: string[]; accordions: string[] } {
        function getGap(ids: string[], currentId: string | undefined, nextId: string): string[] {
            const currentIndex = currentId ? ids.indexOf(currentId) : -1;
            const nextIndex = ids.indexOf(nextId);

            if (nextIndex === -1) return ids;
            if (currentIndex === -1) return ids.slice(0, nextIndex);
            if (currentIndex < nextIndex) return ids.slice(currentIndex + 1, nextIndex);
            return [];
        }

        const containerIds = containers.map(container => container.id);
        const containerGaps = getGap(containerIds, currentContainerId, nextContainerId);

        const allAccordions: string[] = [];
        const startContainerIndex = containerIds.indexOf(currentContainerId);
        const endContainerIndex = containerIds.indexOf(nextContainerId);

        if (startContainerIndex !== -1 && endContainerIndex !== -1) {
            const relevantContainers = containerIds.slice(
                Math.min(startContainerIndex, endContainerIndex),
                Math.max(startContainerIndex, endContainerIndex) + 1
            );
            for (const containerId of relevantContainers) {
                const accordionList = accordionsPerContainer[containerId];
                allAccordions.push(...accordionList.map(accordion => accordion.id));
            }
        }

        const accordionGaps = getGap(allAccordions, currentAccordionId, nextAccordionId);

        return { containers: containerGaps, accordions: accordionGaps };
    }

    private remainingIds(
        currentContainerId: string,
        currentAccordionId: string,
        containers: { id: string }[],
        accordionsPerContainer: { [key: string]: { id: string }[] }
    ): { containers: string[]; accordions: string[] } {
        const containerIds = containers.map(container => container.id);

        const currentContainerIndex = containerIds.indexOf(currentContainerId);

        if (currentContainerIndex === -1) {
            return {
                containers: containerIds,
                accordions: containerIds.flatMap(id => accordionsPerContainer[id].map(a => a.id))
            };
        }

        const remainingContainers = containerIds.slice(currentContainerIndex);

        const remainingAccordions: string[] = [];
        const currentAccordions = accordionsPerContainer[currentContainerId].map(a => a.id);
        const currentAccordionIndex = currentAccordions.indexOf(currentAccordionId);

        if (currentAccordionIndex !== -1) {
            remainingAccordions.push(...currentAccordions.slice(currentAccordionIndex));
        }

        for (const containerId of remainingContainers.slice(1)) {
            const accordions = accordionsPerContainer[containerId].map(a => a.id);
            remainingAccordions.push(...accordions);
        }

        return { containers: remainingContainers, accordions: remainingAccordions };
    }

    private recalculateContainersOnBack(nextContainerBlockId: string, nextAccordionBlockId: string): void {
        // Recalculate containers and accordions to uhhide them if needed
        const accordionsPerContainer: { [key: string]: Block[] } = {};
        const containerBlocks = [...this.getContainerBlocks()];
        containerBlocks.forEach(containerBlock => {
            accordionsPerContainer[containerBlock.id] = [...this.getAccordionBlocks(containerBlock)];
        });
        const remainingIds = this.remainingIds(nextContainerBlockId, nextAccordionBlockId, containerBlocks, accordionsPerContainer);
        remainingIds.containers.forEach(id => {
            this._hiddenContainers[id] = false;
        });
        remainingIds.accordions.forEach(id => {
            this._hiddenAccordions[id] = false;
        });
    }

    private recalculateContainersOnNext(
        currentContainerBlockId: string,
        nextContainerBlockId: string,
        currentAccordionBlockId: string,
        nextAccordionBlockId: string
    ): void {
        // Recalculate containers and accordions to hide some of them if needed
        const accordionsPerContainer: { [key: string]: Block[] } = {};
        const containerBlocks = [...this.getContainerBlocks()];
        containerBlocks.forEach(containerBlock => {
            accordionsPerContainer[containerBlock.id] = [...this.getAccordionBlocks(containerBlock)];
        });
        const nextGaps = this.nextGapIds(
            currentContainerBlockId,
            nextContainerBlockId,
            currentAccordionBlockId,
            nextAccordionBlockId,
            containerBlocks,
            accordionsPerContainer
        );
        nextGaps.containers.forEach(id => {
            this._hiddenContainers[id] = true;
        });
        nextGaps.accordions.forEach(id => {
            this._hiddenAccordions[id] = true;
        });
    }

    private evaluateDecisionBlocks(accordionId: string): void {
        // Reset decision resolution and hide all decision branches before calculating active path.
        this._decisionBlocksOutput = {};
        this._reversedDecisionBlocksOutput = {};
        this._decisionBlocks.forEach(block => {
            block.outputPaths?.forEach(pathId => {
                this.setDecisionBlockPathVisibility(pathId, block.parentId!, true);
            });
        });

        const visited = new Set<string>();
        const evaluateFrom = (block: Block | undefined): void => {
            if (!block || visited.has(block.id) || block.blockType === BlockType.EndOfTheFlowBlock) {
                return;
            }

            visited.add(block.id);

            if (block.blockType === BlockType.DecisionBlock) {
                const nextBlockId = this.setDecisionBlockOutput(block);
                evaluateFrom(this._blocks.find(b => b.id === nextBlockId));
                return;
            }

            evaluateFrom(this._blocks.find(b => b.id === block.outputPaths?.[0]));
        };

        const accordionBlocks = this._blocks.filter(block => block.parentId === accordionId);
        const roots = accordionBlocks
            .filter(block => {
                if (!block.inputPaths?.length) {
                    return true;
                }

                return !block.inputPaths.some(inputId => {
                    const inputBlock = this._blocks.find(b => b.id === inputId);
                    return inputBlock?.parentId === accordionId;
                });
            })
            .sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER));

        if (!roots.length) {
            evaluateFrom(this._firstQuestionBlock);
            return;
        }

        roots.forEach(root => {
            evaluateFrom(root);
        });
    }

    private resetDecisionBlocksOutput(accordionBlock: Block) {
        accordionBlock.childIds?.forEach(childId => {
            if (this._reversedDecisionBlocksOutput[childId]) {
                delete this._decisionBlocksOutput[this._reversedDecisionBlocksOutput[childId]];
                delete this._reversedDecisionBlocksOutput[childId];
                this.hiddenBlocks[childId] = true;
            }
        });
    }

    private getAccordionBlocksIdsFlowAfter(targetAccordionId: string): string[] {
        const ids = this._accordionBlocksIdsFlow;
        const index = ids.findIndex(id => id === targetAccordionId);

        if (index === -1 || index === ids.length - 1) {
            return [];
        }

        return ids.slice(index + 1);
    }

    private getAccordionBlocksIdsFlowUntil(targetId: string): string[] {
        const ids = this._accordionBlocksIdsFlow;
        const index = ids.findIndex(id => id === targetId);

        if (index === -1) {
            return [];
        }

        return ids.slice(0, index + 1);
    }

    private initQuestionsPath(): void {
        this.resetQuestionPath();
        this._questionsPath.push(this._firstQuestionBlock);
        this.calculateQuestionPath(this._firstQuestionBlock);
    }

    private resetQuestionPath(): void {
        this._questionsPath = [];
    }

    private calculateQuestionPath(block: Block): void {
        if (!block || block.blockType === BlockType.EndOfTheFlowBlock) {
            return;
        }

        if (block.blockType === BlockType.DecisionBlock) {
            const resultBlockId = this.getOutputIdAfterDecisionBlockBasedOnDefaultValues(block);
            if (resultBlockId) {
                const resultBlock = this._blocks.find(b => b.id === resultBlockId)!;

                this.calculateQuestionPath(resultBlock);
            }
        } else {
            this._questionsPath.push(block);

            if (block.blockType === BlockType.QuestionBlock) {
                const nextQuestionBlockId = block.outputPaths?.[0]!;
                const nextQuestionBlock = this._blocks.find(b => b.id === nextQuestionBlockId);

                this.calculateQuestionPath(nextQuestionBlock!);
            }
        }
    }

    /**
     * Evaluates isDisabled expressions for all active slots and pushes the
     * result into per-slot signals (via {@link getSlotDisabledSignal}).
     *
     * Why this exists instead of relying on the `isDisabled` getter in SlotComponent:
     * Slot components use OnPush change detection. A plain getter (`get isDisabled`)
     * is only re-evaluated when Angular runs change detection for that component.
     * With OnPush, CD only runs when an @Input changes or an event originates from
     * the component. When a *different* slot (e.g. a stepper) triggers
     * `onQuestionValueChange`, the sibling slots' templates are never re-checked,
     * so their `[disabled]` bindings go stale.
     *
     * By writing into a per-slot WritableSignal here, the effect in SlotComponent
     * (which tracks that signal) is notified and calls `markForCheck()`, ensuring
     * the template re-renders with the correct disabled state.
     */
    private recalculateSlotsDisabledState(): void {
        this._questionsPath
            .filter(block => block.blockType === BlockType.QuestionBlock)
            .flatMap(block => block.template?.slots || [])
            .forEach(slot => {
                const disabled = slot.isDisabled ? !!this._contextModelService.processJsonLogic(slot.isDisabled) : false;
                this.getSlotDisabledSignal(slot.id).set(disabled);
            });
    }

    private reset(): void {
        this._accordionBlocksIdsFlow = [];
        this._flowData = undefined;
        this._blocks = [];
        this._decisionBlocksOutput = {};
        this._reversedDecisionBlocksOutput = {};
        this._decisionBlocks = [];
        this._hiddenContainers = {};
        this._hiddenAccordions = {};
        this._hiddenBlocks = {};
        this._questionsPath = [];
        this.setCurrentContainerBlockId('');
        this.setCurrentAccordionBlockId('');
        this._pathSession = undefined;
        this._contextModelService.session = undefined;
        this._compiledFlowBlocks = undefined;
        this.invalidConfiguration.set(false);
        this._slotsDisabledSignals.forEach(sig => sig.set(false));
        this._slotsDisabledSignals.clear();
    }
}
